import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as EyeIcon } from '../../../assets/icons/eye-icon.svg';
import { ReactComponent as LightningIcon } from '../../../assets/icons/lightning-bolt-icon.svg';
import { ApiRequest } from '../../../interface/common.interface';
import { CardPool } from '../../../interface/rfid-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableCardPoolFilterApplied,
  fetchCardPoolFiltersApi,
  fetchCardPoolsApi,
  setCardPoolSortData,
  setSelectedCardPool,
  updateCardPoolFilters,
} from '../../../redux/slices/CardGroupSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import rfidApi from '../../../utils/api/rfid';

interface CardGroupsListProperties {
  onOpenAddModal?: () => void;
  onOpenRechargeModal: (group: CardPool) => void;
  onOpenDetailsModal: (group: CardPool, isEdit?: boolean) => void;
  onOpenEditModal?: (group: CardPool) => void;
  rightTopContent?: React.JSX.Element;
  sidePanelEnabled?: boolean;
  sidePanelManualContent?: React.JSX.Element;
  sidePanelTopLeftContent?: React.JSX.Element;
  onSidePanelClose?: () => void;
}

/**
 * Card Groups list view component
 * @param {CardGroupsListProperties} properties - Component properties
 * @returns {React.JSX.Element} Card Groups list view with CustomTable
 */
function CardGroupsList(properties: CardGroupsListProperties): React.JSX.Element {
  const {
    onOpenRechargeModal,
    onOpenDetailsModal,
    onOpenEditModal,
    rightTopContent,
    sidePanelEnabled,
    sidePanelManualContent,
    sidePanelTopLeftContent,
    onSidePanelClose,
  } = properties;
  const { t } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    cardPools,
    loading,
    limit,
    page,
    totalPages,
    totalResults,
    filters,
    isFilterUpdated,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.cardGroups);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchCardPoolsApi({
          page: 1,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableCardPoolFilterApplied());
    }
  }, [isFilterUpdated, dispatch, filters, limit, sortBy, sortField]);

  /**
   * Refreshes the card groups list
   * @returns {void}
   */
  const handleRefresh = (): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchCardPoolsApi({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Handles table sort change
   * @param {{ sortBy: 'asc' | 'desc'; sortField: string }} sortData - Sort data with field and order
   * @returns {void}
   */
  const handleSortChange = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setCardPoolSortData(sortData));
    dispatch(
      fetchCardPoolsApi({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: 1,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and page limit change
   * @param {ApiRequest} value - Pagination parameters
   * @returns {void}
   */
  const handlePageLimitChange = (value: ApiRequest): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchCardPoolsApi({
        ...queryParameters,
        page: value.page ?? page,
        limit: value.limit ?? limit,
        sortBy,
        sortField,
      }),
    );
  };

  const columns = [
    {
      label: t('groupNameOrId'),
      accessorKey: 'name' as keyof CardPool,
      // sortAllow: true,
      /**
       * Custom rendering for Group Name and ID
       * @param {CardPool} row - Current row
       * @returns {React.JSX.Element} Group name block
       */
      customData: (row: CardPool): React.JSX.Element => (
        <div
          role="presentation"
          className="rfid-table-group-cell"
          onClick={() => {
            dispatch(setSelectedCardPool(row));
            onOpenDetailsModal(row, false);
          }}>
          <span className="rfid-table-group-cell__title">
            {row.name || (row.kind === 'INDIVIDUAL' ? t('individualTapGroup') : t('group'))}
          </span>
          <span className="rfid-table-group-cell__id">{row.id}</span>
        </div>
      ),
    },
    {
      label: t('groupKind'),
      accessorKey: 'kind' as keyof CardPool,
      // sortAllow: true,
      /**
       * Custom rendering for Group Kind
       * @param {CardPool} row - Current row
       * @returns {React.JSX.Element} Group kind badge
       */
      customData: (row: CardPool): React.JSX.Element => {
        const isGroup = row.kind === 'GROUP';
        return (
          <span
            className={`rfid-kind-badge ${
              isGroup ? 'rfid-kind-badge--group' : 'rfid-kind-badge--individual'
            }`}>
            {row.kind}
          </span>
        );
      },
    },
    {
      label: t('balanceKwh'),
      accessorKey: 'balanceKwh' as keyof CardPool,
      // sortAllow: true,
      /**
       * Custom rendering for Energy Balance
       * @param {CardPool} row - Current row
       * @returns {React.JSX.Element} Balance badge
       */
      customData: (row: CardPool): React.JSX.Element => (
        <div className="rfid-table-balance-cell">
          <span className="numeric-unit" dir="ltr">
            <span className="rfid-table-balance-cell__value">
              {Number(row.balanceKwh).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>{' '}
            <span className="rfid-table-balance-cell__unit">kWh</span>
          </span>
        </div>
      ),
    },
    {
      label: t('authorizedStations'),
      accessorKey: 'stationIds' as keyof CardPool,
      // sortAllow: false,
      /**
       * Custom rendering for Authorized Stations
       * @param {CardPool} row - Current row
       * @returns {React.JSX.Element} Stations tags
       */
      customData: (row: CardPool): React.JSX.Element => (
        <div className="rfid-table-stations-cell">
          <span className="rfid-table-stations-cell__count">
            {row.stationIds.length}{' '}
            {row.stationIds.length === 1 ? t('station') : t('stationsCount')}
          </span>
          {row.stationNames && row.stationNames.length > 0 && (
            <span className="rfid-table-stations-cell__preview">
              {row.stationNames.slice(0, 2).join(', ')}
              {row.stationNames.length > 2 && ' ...'}
            </span>
          )}
        </div>
      ),
    },
    {
      label: t('linkedCustomer'),
      accessorKey: 'linkedCustomerId' as keyof CardPool,
      // sortAllow: true,
      /**
       * Custom rendering for Linked Customer ID
       * @param {CardPool} row - Current row
       * @returns {React.JSX.Element} Customer link
       */
      customData: (row: CardPool): React.JSX.Element => (
        <span className="rfid-table-text-muted">
          {row.linkedCustomerId ? (
            <span className="rfid-table-customer-tag">{row.linkedCustomerId}</span>
          ) : (
            'N/A'
          )}
        </span>
      ),
    },
    {
      label: t('cardsCountColumn'),
      accessorKey: 'tokenCount' as keyof CardPool,
      // sortAllow: true,
      /**
       * Custom rendering for Cards Count
       * @param {CardPool} row - Current row
       * @returns {React.JSX.Element} Cards count badge
       */
      customData: (row: CardPool): React.JSX.Element => (
        <span className="rfid-table-cards-count">
          {row.tokenCount || (row.kind === 'GROUP' ? 2 : 1)} {t('cards')}
        </span>
      ),
    },
    {
      label: t('updatedAt'),
      accessorKey: 'updatedAt' as keyof CardPool,
      // sortAllow: true,
      /**
       * Custom rendering for Last Updated
       * @param {CardPool} row - Current row
       * @returns {React.JSX.Element} Last updated timestamp
       */
      customData: (row: CardPool): React.JSX.Element => (
        <span className="rfid-table-text-muted">
          {row.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : t('na')}
        </span>
      ),
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof CardPool,
      columnWidth: 120,
      // sortAllow: false,
      sticky: 'right' as const,
      /**
       * Action buttons for editing group, recharging balance, and viewing details
       * @param {CardPool} row - Current row
       * @returns {React.JSX.Element} Action buttons element
       */
      customData: (row: CardPool): React.JSX.Element => (
        <div className="action-buttons-wrap">
          <div
            role="presentation"
            className="action-button"
            title={t('editGroup')}
            onClick={(event) => {
              event.stopPropagation();
              dispatch(setSelectedCardPool(row));
              if (onOpenEditModal) {
                onOpenEditModal(row);
              } else {
                onOpenDetailsModal(row, true);
              }
            }}>
            <EditIcon className="icon edit" />
          </div>
          <div
            role="presentation"
            className="action-button"
            title={t('rechargeBalance')}
            onClick={(event) => {
              event.stopPropagation();
              dispatch(setSelectedCardPool(row));
              onOpenRechargeModal(row);
            }}>
            <LightningIcon className="icon recharge" />
          </div>
          <div
            role="presentation"
            className="action-button"
            title={t('viewDetails')}
            onClick={(event) => {
              event.stopPropagation();
              dispatch(setSelectedCardPool(row));
              onOpenDetailsModal(row, false);
            }}>
            <EyeIcon className="icon view" />
          </div>
        </div>
      ),
    },
  ];

  const allExportColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('groupName'), accessorKey: 'name' },
    { label: t('groupKind'), accessorKey: 'kind' },
    { label: t('sharedBalanceKwh'), accessorKey: 'balanceKwh' },
    { label: t('authorizedStations'), accessorKey: 'stationIds' },
    {
      label: t('linkedCustomer'),
      accessorKey: 'linkedCustomerId',
    },
    { label: t('cardsCountExport'), accessorKey: 'tokenCount' },
    { label: t('updatedAt'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filter columns for export without hidden columns
   */
  const exportCurrentColumns = allExportColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export card groups data to CSV or PDF format
   * @param {string} type - Export file format ('csv' | 'pdf')
   * @returns {void}
   */
  const exportDataPdfOrCSV = (type: string): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    setExportLoader(true);
    rfidApi
      .fetchCardPoolsExport(removeEmptyKeys(payload))
      .then((response: any) => {
        const rawResults =
          response?.data?.results ||
          response?.data?.data ||
          (Array.isArray(response?.data) ? response?.data : []);
        const dataToExport =
          Array.isArray(rawResults) && rawResults.length > 0 ? rawResults : cardPools;

        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = dataToExport.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);
              case 'name':
                return (
                  row.name ||
                  (row.kind === 'INDIVIDUAL'
                    ? t('individualTapGroup')
                    : row.id || row._id || t('na'))
                );
              case 'kind':
                return row.kind || 'GROUP';
              case 'balanceKwh': {
                let balance = 0;
                const rawBal = row.balanceKwh;
                if (typeof rawBal === 'object' && rawBal !== null && '$numberDecimal' in rawBal) {
                  balance = Number.parseFloat(rawBal.$numberDecimal) || 0;
                } else if (typeof rawBal === 'number') {
                  balance = rawBal;
                } else if (typeof rawBal === 'string') {
                  balance = Number.parseFloat(rawBal) || 0;
                }
                return `${balance.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} kWh`;
              }
              case 'stationIds':
                if (Array.isArray(row.stationNames) && row.stationNames.length > 0) {
                  return row.stationNames.join(', ');
                }
                return `${row.stationIds?.length || 0} ${t('stationsCount')}`;
              case 'linkedCustomerId':
                return row.linkedCustomerId || t('na');
              case 'tokenCount':
                return String(row.tokenCount || (row.kind === 'GROUP' ? 2 : 1));
              case 'updatedAt':
                return row.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : t('na');
              default:
                return String((row as any)[col.accessorKey] || 'N/A');
            }
          }),
        );

        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'card-groups.csv');
          } else {
            exportPDF(headers, rows, 'card-groups.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('exportFailed'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 500,
          }),
        );
      });
  };

  return (
    <CustomTable
      version="v2"
      header={columns}
      data={cardPools}
      loading={loading}
      limit={limit}
      page={page}
      totalPages={totalPages}
      totalResults={totalResults}
      serialNo
      sortData={{ sortBy, sortField }}
      handleSortChange={handleSortChange}
      pageLimitChange={handlePageLimitChange}
      filters={filters}
      filtersToDispatch={updateCardPoolFilters}
      filterListToDispatch={fetchCardPoolFiltersApi}
      filterListLoading={filterListLoading}
      refreshRequired
      handleRefresh={handleRefresh}
      refershCurrentPages={handleRefresh}
      hoverLabel={t('refresh')}
      sizeChangeButtonRequired
      rightTopContent={rightTopContent}
      sidePanelEnabled={sidePanelEnabled}
      sidePanelManualContent={sidePanelManualContent}
      sidePanelTopLeftContent={sidePanelTopLeftContent}
      onSidePanelClose={onSidePanelClose}
      onHiddenColumnsChange={setHiddenColumns}
      exportDataPdfOrCSV={exportDataPdfOrCSV}
      exportLoader={exportLoader}
      exportData={cardPools}
      isExportRequired
    />
  );
}

export default CardGroupsList;
